import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Platform, Pressable as PresabelRN } from 'react-native'

import { publishEvent } from '@zet/analytics'
import { BottomSheet, H6, Icon, SH3, Stack, XStack } from '@zet/design-system'
import { Checkbox } from '@zet/design-system/checkbox'
import Pressable from '@zet/design-system/pressable'
import { sendAnalyticsData } from '@zet/integration-flow/utils/analyticsEvents'
import useGetAgentDetailsAndTheme from '@zet/zet-pro-quick-journey/hooks/useGetAgentDetailsAndTheme'

import useDropDownBSStore from '../../hooks/useFilterStore'
import RadioButton from '../radioButton'

export type DropDownFilterData = {
  key: string
  type: 'SUB_FILTER' | 'DROPDOWN' | 'QUICK_FILTER'
  displayText: string
  isMultiSelect?: boolean
  values?: {
    isDefault: boolean | undefined
    key: 'BRANDS'
    displayText: string
    isMultiSelect: boolean
  }[]
}

export type DropDownFilterState = string[] | string | undefined

type DropDownFiltersBottomSheetProps = {
  customerId?: string | number
  categoryId?: string | number
  data?: DropDownFilterData
  onDismiss?: () => void
  onChange?: (selectedFilters: DropDownFilterState) => void
}

const DropDownFilterBottomSheet = (
  {
    data,
    onDismiss,
    onChange,
    customerId,
    categoryId,
  }: DropDownFiltersBottomSheetProps,
  ref
) => {
  const [isVisible, setIsVisible] = useState(false)
  const { isBMSFlow, isJfsFlow } = useGetAgentDetailsAndTheme()

  const { sort: selectedFilter, setSort: setSelectedFilter } =
    useDropDownBSStore((state) => ({
      sort: state?.sort,
      setSort: state?.setSort,
    }))

  const _onDismiss = () => {
    setIsVisible(false)
    onDismiss?.()
  }

  const optionSelectEvent = (filterKey?: string) => {
    if (isJfsFlow) {
      sendAnalyticsData({
        screen: 'SORTSCREEN_SCREEN_CTA',
        type: 'click',
        optional_data: {
          option: filterKey,
        },
      })
    }

    if (filterKey) {
      publishEvent('click_action', {
        item_name: `sort_option_new_${
          Platform.OS === 'web'
            ? 'recommendation_web'
            : customerId
            ? 'recommendation_app'
            : 'brand_list'
        }`,
        product: categoryId,
        option: filterKey,
      })
    }
  }

  const onChangeHandler = ({
    isSelected,
    isMultiSelect,
    filterKey,
  }: {
    isSelected: boolean
    filterKey: string
    isMultiSelect: boolean
  }) => {
    let updatedFilterValues

    if (isSelected) {
      // Remove the selected filter(s) based on isMultiSelect flag
      updatedFilterValues =
        isMultiSelect && Array.isArray(selectedFilter)
          ? selectedFilter?.filter((_filterKey) => _filterKey !== filterKey)
          : undefined // Set to undefined if it's not multi-select
    } else {
      // Add the selected sub-filter(s) based on isMultiSelect flag
      updatedFilterValues =
        isMultiSelect && Array.isArray(selectedFilter)
          ? [...selectedFilter, filterKey]
          : filterKey
    }

    setSelectedFilter(updatedFilterValues)
  }

  useImperativeHandle(
    ref,
    () => {
      return {
        resetLocalState() {
          setSelectedFilter(data?.isMultiSelect ? [] : '')
        },
        updateLcalState(filterState: string) {
          setSelectedFilter(filterState)
        },
        show: () => setIsVisible(true),
        hide: () => setIsVisible(false),
      }
    },
    [data?.isMultiSelect, setSelectedFilter]
  )

  useEffect(() => {
    if (Number(selectedFilter?.length) > 0) {
      onChange?.(selectedFilter)
    }
  }, [onChange, selectedFilter])

  return (
    <BottomSheet visible={isVisible} onDismiss={_onDismiss}>
      <Stack className="">
        {/* HEADER SECTION */}
        <XStack
          key={data?.key}
          className=" border-grays-30 items-center justify-between border-b px-4 py-2"
        >
          <XStack className="items-center justify-center">
            <Icon type="Sorting" className="mr-4/2" />
            <H6>{data?.displayText}</H6>
          </XStack>
          <PresabelRN
            onPress={() => {
              setIsVisible(false)
            }}
          >
            <Icon type="Close" />
          </PresabelRN>
        </XStack>
        {/* HEADER SECTION ENDS */}

        <Stack className="space-y-2 p-3 pb-4">
          {data?.values?.map((filter) => {
            const isMultiSelect = !!data?.isMultiSelect
            const isSelected = !!selectedFilter?.includes?.(filter?.key)
            return (
              <Pressable
                key={filter?.key}
                onPress={() => {
                  onChangeHandler({
                    isSelected,
                    isMultiSelect,
                    filterKey: filter?.key,
                  })
                  !isSelected && optionSelectEvent(filter?.key)
                }}
              >
                <XStack
                  className={`${
                    isJfsFlow ? 'bg-grays-20' : 'bg-primary-10'
                  } flex-row items-center  rounded border  p-3 ${
                    isSelected
                      ? `${
                          isBMSFlow
                            ? 'border-primary-100'
                            : isJfsFlow
                            ? 'border-primary-100 bg-white'
                            : 'border-primary-50'
                        }`
                      : 'border-transparent'
                  }`}
                >
                  {isMultiSelect ? (
                    <Checkbox
                      accesibilityLabel={filter?.displayText}
                      checked={isSelected}
                      onChange={() =>
                        onChangeHandler({
                          isSelected,
                          isMultiSelect,
                          filterKey: filter?.key,
                        })
                      }
                    />
                  ) : (
                    <RadioButton
                      isChecked={
                        selectedFilter?.length > 0
                          ? isSelected
                          : filter?.isDefault
                      }
                      size={24}
                    />
                  )}

                  <SH3
                    className={`ml-2 ${
                      isSelected ? 'text-primary-100' : 'text-grays-80'
                    }`}
                  >
                    {filter?.displayText}
                  </SH3>
                </XStack>
              </Pressable>
            )
          })}
        </Stack>
      </Stack>
    </BottomSheet>
  )
}

export default forwardRef(DropDownFilterBottomSheet)
